// ============================================================
// TMR Portal Admin — leituras e escritas dos modelos de contrato, sobre @tmr/api-client
// ============================================================
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { api } from '@tmr/api-client';
import { contractTemplateKeys } from '../queryClient';
import type {
  ContractTemplateDetail,
  ContractTemplateList,
  CreateContractTemplateRequest,
  DocumentType,
  LegacyImportReport,
  TemplateDiff,
  TemplatePreview,
  TemplateVersion,
  TemplateWriteResult,
} from './contract';

/**
 * Every template, global and override, with the document types that have no global text yet.
 *
 * Unpaged, and that is the backend's decision rather than an omission here: there is one global template
 * per type plus one override per clinic that negotiated a different text (`24` §9).
 *
 * @param enabled Whether the operator holds `contract.templates.read` — false skips a request that would 403.
 */
export function useContractTemplates(enabled = true): UseQueryResult<ContractTemplateList> {
  return useQuery({
    queryKey: contractTemplateKeys.list(),
    queryFn: ({ signal }) => api.get<ContractTemplateList>('/contract-templates', { signal }),
    enabled,
  });
}

/**
 * One template with every version's text and the type's variables.
 *
 * @param templateId The template, or undefined while the route has none.
 */
export function useContractTemplate(
  templateId: string | undefined,
): UseQueryResult<ContractTemplateDetail> {
  return useQuery({
    queryKey: contractTemplateKeys.detail(templateId ?? ''),
    queryFn: ({ signal }) =>
      api.get<ContractTemplateDetail>(`/contract-templates/${templateId}`, { signal }),
    enabled: Boolean(templateId),
  });
}

/**
 * What the v1.1 holds that is not here yet, and what the initial load would do with each template.
 *
 * Read on the templates screen because the answer changes what the screen may offer: a document type
 * whose global exists only in the v1.1 must not be offered as "create the global", since the copy of a
 * second one would be refused over there forever (`24` P2b). Not retried: a 503 means this environment
 * has no legacy connection, which is an answer, not a failure.
 *
 * @param enabled Whether the operator may read templates at all.
 */
export function useLegacyImportPreview(enabled = true): UseQueryResult<LegacyImportReport> {
  return useQuery({
    queryKey: contractTemplateKeys.legacyImport(),
    queryFn: ({ signal }) => api.get<LegacyImportReport>('/contract-templates/legacy-import', { signal }),
    enabled,
    retry: false,
  });
}

/**
 * Brings here the templates the v1.1 already has. Safe to run again — what is already here is left alone.
 *
 * Refreshes everything about templates afterwards: the list gains the imported ones and the preview
 * shrinks to what is still waiting.
 */
export function useImportLegacyTemplates(): UseMutationResult<LegacyImportReport, Error, void> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<LegacyImportReport>('/contract-templates/legacy-import', {}),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: contractTemplateKeys.all });
    },
  });
}

/**
 * The document types and their variables.
 *
 * Long-lived in cache on purpose: the catalogue is code on the backend, identical for every operator, and
 * changes only with a deploy.
 *
 * @param enabled Whether the operator may read templates at all.
 */
export function useDocumentTypes(enabled = true): UseQueryResult<DocumentType[]> {
  return useQuery({
    queryKey: contractTemplateKeys.types(),
    queryFn: ({ signal }) => api.get<DocumentType[]>('/contract-templates/types', { signal }),
    enabled,
    staleTime: 15 * 60 * 1000,
  });
}

/** Creates a template with an empty first draft — global, or one clinic's override. */
export function useCreateContractTemplate(): UseMutationResult<
  TemplateWriteResult,
  Error,
  CreateContractTemplateRequest
> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (request) => api.post<TemplateWriteResult>('/contract-templates', request),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: contractTemplateKeys.all });
    },
  });
}

/** Opens a draft carrying the text of the version in force. */
export function useOpenDraft(): UseMutationResult<
  TemplateWriteResult,
  Error,
  { templateId: string; expectedVersion: number }
> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, expectedVersion }) =>
      api.post<TemplateWriteResult>(`/contract-templates/${templateId}/versions`, { expectedVersion }),
    onSuccess: (_, { templateId }) => {
      void client.invalidateQueries({ queryKey: contractTemplateKeys.detail(templateId) });
      void client.invalidateQueries({ queryKey: contractTemplateKeys.list() });
    },
  });
}

/**
 * Saves the draft's text.
 *
 * **It does not invalidate the detail.** This runs on every `Ctrl+S`, and refetching would replace the
 * text under the operator's cursor with the copy that has just been sent — the editor already holds what
 * it wrote, and what it needs back is only the new write version.
 *
 * It does drop the template's comparisons: they were computed from the draft as it was stored before, and
 * the publish dialog opened right after a save would otherwise show what the previous save changed.
 */
export function useSaveDraft(): UseMutationResult<
  TemplateWriteResult,
  Error,
  { templateId: string; versionId: string; content: string; expectedVersion: number }
> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, versionId, content, expectedVersion }) =>
      api.put<TemplateWriteResult>(
        `/contract-templates/${templateId}/versions/${versionId}`,
        { content, expectedVersion },
      ),
    onSuccess: (_, { templateId }) => {
      void client.invalidateQueries({ queryKey: [...contractTemplateKeys.detail(templateId), 'diff'] });
    },
  });
}

/** Puts the draft into force. The note is required — it is what the history reads. */
export function usePublishVersion(): UseMutationResult<
  TemplateWriteResult,
  Error,
  { templateId: string; versionId: string; note: string; expectedVersion: number }
> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, versionId, note, expectedVersion }) =>
      api.post<TemplateWriteResult>(
        `/contract-templates/${templateId}/versions/${versionId}/publication`,
        { note, expectedVersion },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: contractTemplateKeys.all });
    },
  });
}

/** Takes a clinic's override out of use. The global template cannot be archived. */
export function useArchiveTemplate(): UseMutationResult<
  TemplateWriteResult,
  Error,
  { templateId: string; expectedVersion: number }
> {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, expectedVersion }) =>
      api.post<TemplateWriteResult>(`/contract-templates/${templateId}/archival`, { expectedVersion }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: contractTemplateKeys.all });
    },
  });
}

/**
 * Renders the text in the editor.
 *
 * **A mutation rather than a query, on purpose.** It is a `POST` that writes nothing, and the thing being
 * rendered is unsaved text the operator is still typing — there is no key it could be cached under that
 * would not be stale by the next keystroke.
 */
export function usePreviewTemplate(): UseMutationResult<
  TemplatePreview,
  Error,
  { documentType: string; content: string }
> {
  return useMutation({
    mutationFn: (request) => api.post<TemplatePreview>('/contract-templates/preview', request),
  });
}

/**
 * A **published** version, rendered with the type's example values — what the history shows as "Texto".
 *
 * The same rendering as the editor's preview, asked as a query rather than a mutation: a published version
 * is immutable, so its rendering is cached for good under the version's id. The editor's own text is the
 * opposite case and stays a mutation.
 *
 * @param templateId The template.
 * @param version The version to render; undefined while none is open.
 * @param documentType The template's document type, whose example values fill the markers.
 */
export function useRenderedVersion(
  templateId: string,
  version: TemplateVersion | undefined,
  documentType: string,
): UseQueryResult<TemplatePreview> {
  return useQuery({
    queryKey: contractTemplateKeys.rendered(templateId, version?.id ?? ''),
    queryFn: () =>
      api.post<TemplatePreview>('/contract-templates/preview', {
        documentType,
        content: version?.content ?? '',
      }),
    enabled: Boolean(version),
    staleTime: Infinity,
  });
}

/**
 * Prints the text in the editor to PDF and opens it in a new tab.
 *
 * **A fetch rather than a link, unlike the other file endpoints.** This one is a POST — the text being
 * printed is unsaved and travels in the body — so there is no URL an anchor could point at. The blob is
 * revoked once the tab has it.
 *
 * A `503` means this environment has no renderer; the caller says so and the editor carries on (`24` §9).
 */
export function usePreviewTemplatePdf(): UseMutationResult<
  void,
  Error,
  { documentType: string; name: string; content: string }
> {
  return useMutation({
    mutationFn: async (request) => {
      const blob = await api.postForBlob('/contract-templates/preview/pdf', request);
      const url = URL.createObjectURL(blob);

      window.open(url, '_blank', 'noopener');
      // Long enough for the new tab to have fetched it; the browser reclaims the object either way when
      // the page goes.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
  });
}

/**
 * What changed between two versions of a template.
 *
 * @param templateId The template.
 * @param versionId The version being looked at — usually the draft.
 * @param againstVersionId What to compare it with; omit for the version in force.
 */
export function useTemplateDiff(
  templateId: string | undefined,
  versionId: string | undefined,
  againstVersionId?: string,
): UseQueryResult<TemplateDiff> {
  return useQuery({
    queryKey: contractTemplateKeys.diff(templateId ?? '', versionId ?? '', againstVersionId ?? ''),
    queryFn: ({ signal }) =>
      api.get<TemplateDiff>(
        `/contract-templates/${templateId}/versions/${versionId}/diff`
          + (againstVersionId ? `?against=${againstVersionId}` : ''),
        { signal },
      ),
    enabled: Boolean(templateId && versionId),
  });
}
